import { keepPreviousData, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";

import { API_ROUTES } from "../../api/ApiRoutes";
import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { getAccessToken } from "../../api/getAccessToken";
import type {
  SgpIdBetResult,
  SgpIdBetRow,
  SgpIdBetStatus,
} from "../../components/Sportsbook/SGPRisk/SGPIdBetsList/sgpIdBetsListData";

export const SGP_RISK_BETS_LIST_QUERY_KEY = "SGP_RISK_BETS_LIST" as const;
export const SGP_ID_BET_DATE_TIME_FORMAT = "DD MMMM YYYY, HH:mm:ss" as const;

export const SGP_RISK_BETS_LIST_SORT_FIELDS = {
  dateTime: "created_at",
  betAmount: "wager_amount",
  payout: "payout",
  odds: "combined_odds",
} as const;

export type SgpRiskBetsListSortBy =
  (typeof SGP_RISK_BETS_LIST_SORT_FIELDS)[keyof typeof SGP_RISK_BETS_LIST_SORT_FIELDS];

export type SgpRiskBetsListStatus = "won" | "lost" | "pending" | "pushed";

type Nullable<T> = T | null | undefined;

export type SgpRiskBetsListParams = {
  sgp_id: string;
  sport_id: string;
  league_id: string;
  start_date?: Nullable<string>;
  end_date?: Nullable<string>;
  page: number;
  page_size: number;
  search_term?: Nullable<string>;
  min_bet_amount?: Nullable<number>;
  min_win_amount?: Nullable<number>;
  min_odds?: Nullable<number>;
  status?: Nullable<SgpRiskBetsListStatus>;
  sort_by?: SgpRiskBetsListSortBy;
  sort_order?: "asc" | "desc";
};

export type SgpRiskBetsListApiLeg = {
  event_id?: string;
  sport_id?: string;
  sport_name?: string;
  league_name?: string;
  league_id?: string;
  tournament_id?: string;
  market_name?: string;
  selection_name?: string;
  odds?: number;
  leg_result?: string;
};

export type SgpRiskBetsListApiBet = {
  bet_id: string;
  created_at: number;
  finalized?: boolean;
  status: string;
  sport_id?: string;
  sport_name?: string;
  league_name?: string;
  money_type?: string;
  wager_amount: number;
  combined_odds: number;
  payout: number;
  user?: {
    user_id?: string;
    username?: string;
    profile_pic_link?: string;
  };
  legs?: SgpRiskBetsListApiLeg[];
};

export type SgpRiskBetsListResult = {
  rows: SgpIdBetRow[];
  pagination: {
    current_page: number;
    page_size: number;
    total_results: number;
    total_pages: number;
    has_next: boolean;
    has_prev: boolean;
  };
  applied_date_range: {
    start: string;
    end: string;
  } | null;
};

const EMPTY_PAGINATION = {
  current_page: 1,
  page_size: 10,
  total_results: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
};

const oddsFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function finiteNumber(value: unknown, fallback = 0): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function optionalNumber(value: Nullable<number>): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function optionalString(value: Nullable<string>): string | null {
  const trimmed = typeof value === "string" ? value.trim() : "";
  return trimmed.length > 0 ? trimmed : null;
}

export function formatSgpIdBetDateTime(epochMs: number): string {
  if (!Number.isFinite(epochMs)) {
    return "";
  }

  return dayjs(epochMs).format(SGP_ID_BET_DATE_TIME_FORMAT);
}

function formatDecimalOdds(value: number): string {
  return oddsFormatter.format(finiteNumber(value));
}

function mapLegResult(value: Nullable<string>): SgpIdBetResult {
  switch (String(value ?? "").toLowerCase()) {
    case "won":
      return "Won";
    case "lost":
      return "Lost";
    case "push":
    case "pushed":
    case "void":
    case "voided":
      return "Push";
    case "pending":
    default:
      return "Pending";
  }
}

function mapBetStatus(status: string, finalized: boolean): SgpIdBetStatus {
  switch (String(status ?? "").toLowerCase()) {
    case "won":
      return "Won";
    case "lost":
      return "Lost";
    case "pushed":
      return "Push";
    case "pending":
      return "Pending";
    default:
      return finalized ? "Lost" : "Pending";
  }
}

function isVoidedLegResult(value: Nullable<string>): boolean {
  const normalized = String(value ?? "").toLowerCase();
  return normalized === "push" || normalized === "pushed" || normalized === "void" || normalized === "voided";
}

export function mapSgpRiskBetsListApiBetToTableRow(
  bet: SgpRiskBetsListApiBet,
  sgpId: string,
): SgpIdBetRow {
  const legs = Array.isArray(bet.legs) ? bet.legs : [];
  const combinedOdds = finiteNumber(bet.combined_odds);

  return {
    id: String(bet.bet_id ?? ""),
    player: String(bet.user?.username ?? ""),
    dateTime: formatSgpIdBetDateTime(finiteNumber(bet.created_at)),
    sport: String(bet.sport_name ?? legs[0]?.sport_name ?? ""),
    league: optionalString(bet.league_name) ?? "-",
    sgpId,
    moneyType: optionalString(bet.money_type) ?? undefined,
    betAmount: finiteNumber(bet.wager_amount),
    winAmount: finiteNumber(bet.payout),
    status: mapBetStatus(bet.status, Boolean(bet.finalized)),
    overallOddsValue: combinedOdds,
    overallOddsDisplay: `(${formatDecimalOdds(combinedOdds)})`,
    selections: legs.map((leg) => ({
      label: String(leg.market_name ?? ""),
      value: String(leg.selection_name ?? ""),
      odds: formatDecimalOdds(finiteNumber(leg.odds)),
      result: mapLegResult(leg.leg_result),
      voided: isVoidedLegResult(leg.leg_result),
    })),
  };
}

function normalizeAppliedDateRange(
  value: unknown,
): SgpRiskBetsListResult["applied_date_range"] {
  if (!value || typeof value !== "object") {
    return null;
  }

  const dateRange = value as { start?: unknown; end?: unknown };
  return typeof dateRange.start === "string" && typeof dateRange.end === "string"
    ? { start: dateRange.start, end: dateRange.end }
    : null;
}

function normalizeListResponse(body: unknown, sgpId: string): SgpRiskBetsListResult {
  const root = body as { data?: Record<string, unknown> } | null | undefined;
  const data = root?.data ?? {};
  const apiBets = Array.isArray(data.bets)
    ? (data.bets as SgpRiskBetsListApiBet[])
    : [];
  const pagination =
    data.pagination && typeof data.pagination === "object"
      ? { ...EMPTY_PAGINATION, ...(data.pagination as object) }
      : EMPTY_PAGINATION;

  return {
    rows: apiBets.map((bet) => mapSgpRiskBetsListApiBetToTableRow(bet, sgpId)),
    pagination,
    applied_date_range: normalizeAppliedDateRange(data.applied_date_range),
  };
}

function buildListRequestBody(params: SgpRiskBetsListParams) {
  return {
    sgp_id: params.sgp_id,
    sport_id: params.sport_id,
    league_id: params.league_id,
    start_date: optionalString(params.start_date),
    end_date: optionalString(params.end_date),
    page: params.page,
    page_size: params.page_size,
    search_term: optionalString(params.search_term),
    min_bet_amount: optionalNumber(params.min_bet_amount),
    min_win_amount: optionalNumber(params.min_win_amount),
    min_odds: optionalNumber(params.min_odds),
    status: optionalString(params.status),
    sort_by: params.sort_by ?? "created_at",
    sort_order: params.sort_order ?? "desc",
  };
}

export async function fetchSgpRiskBetsList(
  params: SgpRiskBetsListParams,
  signal?: AbortSignal,
): Promise<SgpRiskBetsListResult> {
  const instance = createPrivateAdminPanelInstance(getAccessToken());
  const response = await instance.post(
    API_ROUTES.GET_SGP_RISK_BETS_LIST,
    buildListRequestBody(params),
    { signal },
  );

  return normalizeListResponse(response?.data, params.sgp_id);
}

function numberToken(value: Nullable<number>): string {
  return typeof value === "number" && Number.isFinite(value) ? String(value) : "";
}

export const SGP_RISK_BETS_LIST_PAGE_QUERY_KEY_INDEX = 6;

export function resolveSgpRiskBetsListPlaceholderData<T>(
  previousData: T | undefined,
  previousQuery: { queryKey: readonly unknown[] } | undefined,
  nextQueryKey: readonly unknown[],
): T | undefined {
  if (!previousData || !previousQuery) {
    return undefined;
  }

  const previousKey = previousQuery.queryKey;
  const onlyPageChanged = nextQueryKey.every((value, index) => {
    if (index === SGP_RISK_BETS_LIST_PAGE_QUERY_KEY_INDEX) {
      return true;
    }

    return previousKey[index] === value;
  });

  return onlyPageChanged ? keepPreviousData(previousData) : undefined;
}

export function useSgpRiskBetsList(params: SgpRiskBetsListParams) {
  const {
    sgp_id,
    sport_id,
    league_id,
    start_date,
    end_date,
    page,
    page_size,
    search_term,
    min_bet_amount,
    min_win_amount,
    min_odds,
    status,
    sort_by,
    sort_order,
  } = params;

  const queryKey = [
    SGP_RISK_BETS_LIST_QUERY_KEY,
    sgp_id,
    sport_id,
    league_id,
    start_date ?? "",
    end_date ?? "",
    page,
    page_size,
    search_term ?? "",
    numberToken(min_bet_amount),
    numberToken(min_win_amount),
    numberToken(min_odds),
    status ?? "",
    sort_by ?? "created_at",
    sort_order ?? "desc",
  ] as const;

  const hasRequiredContext = Boolean(
    sgp_id?.trim() && sport_id?.trim() && league_id?.trim(),
  );
  const hasValidDateRange =
    (!start_date && !end_date) ||
    Boolean(start_date?.trim() && end_date?.trim());

  return useQuery<SgpRiskBetsListResult>({
    queryKey,
    queryFn: ({ signal }) => fetchSgpRiskBetsList(params, signal),
    enabled: hasRequiredContext && hasValidDateRange,
    placeholderData: (previousData, previousQuery) =>
      resolveSgpRiskBetsListPlaceholderData(previousData, previousQuery, queryKey),
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
}
